import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactElement } from 'react';
import {
  BooleanInput,
  NumberField,
  SearchInput,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';

import {
  GetInstrumentsQuery,
  GetInstrumentsResponseItem,
  getInstrumentsSortColumns,
} from '@Common/types/bo_external/instruments';

import { DatagridTyped, FilterButton, List } from '@sh4res/bo-common/components';
import { fundinfoPricingFrequencyChoices } from '@sh4res/bo-common/components/Instruments/choices';
import { PerformanceFilterButton } from '@sh4res/bo-common/components/Instruments/PerformanceFilterButton';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import { IdField, PerformanceChipField, SelectChipField } from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { createFilters } from '@sh4res/bo-common/utils/create_filters';

const defaultSort: RaSort<GetInstrumentsQuery> = { field: 'id', order: 'DESC' };

const testId = 'BoExternalInstrumentsFundsList';

const instrumentFilters = createFilters<GetInstrumentsQuery>({
  id: <TextInput source="" data-testid={`${testId}_IdInput`} />,
  query: <SearchInput source="" alwaysOn data-testid={`${testId}_QueryInput`} />,
  isin: <TextInput source="" data-testid={`${testId}_IsinInput`} />,
  short_name: <TextInput source="" data-testid={`${testId}_ShortNameInput`} />,
  performance: <TextField data-testid={`${testId}_PerformanceField`} />,
  type: <TextInput source="" data-testid={`${testId}_TypeInput`} />,
  from_redemption_period_start_at: (
    <DateInput source="" data-testid={`${testId}_FromRedemptionPeriodStartInput`} />
  ),
  to_redemption_period_start_at: (
    <DateInput source="" data-testid={`${testId}_ToRedemptionPeriodStartInput`} />
  ),
  from_redemption_period_end_at: (
    <DateInput source="" data-testid={`${testId}_FromRedemptionPeriodEndInput`} />
  ),
  to_redemption_period_end_at: (
    <DateInput source="" data-testid={`${testId}_ToRedemptionPeriodEndInput`} />
  ),
  is_floor_price_underlying: (
    <BooleanInput source="" data-testid={`${testId}_IsFloorPriceUnderlyingInput`} />
  ),
  is_plan_universe_eligible: (
    <BooleanInput source="" data-testid={`${testId}_IsPlanUniverseEligibleInput`} />
  ),
});

const instrumentsListColumns: ColumnList<GetInstrumentsResponseItem> = {
  id: <IdField reference="instruments" data-testid={`${testId}_IdField`} />,
  short_name: <TextField data-testid={`${testId}_ShortNameField`} />,
  isin: <TextField data-testid={`${testId}_IsinField`} />,
  risk: <NumberField data-testid={`${testId}_RiskField`} />,
  management_company: <TextField data-testid={`${testId}_ManagementCompanyField`} />,
  pricing_frequency: (
    <SelectChipField
      choices={fundinfoPricingFrequencyChoices}
      data-testid={`${testId}_PricingFrequencyField`}
    />
  ),
  performance: <PerformanceChipField data-testid={`${testId}_PerformanceChipField`} />,
};

const defaultColumns: (keyof GetInstrumentsResponseItem)[] = [
  'id',
  'short_name',
  'isin',
  'management_company',
  'risk',
  'pricing_frequency',
  'performance',
];

const InstrumentsActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

type InstrumentsActionsProps<T extends Record<string, unknown>> = {
  defaultColumns: (keyof T)[];
  columns: Record<keyof T, ReactElement>;
  preferenceKey: string;
  showPerformanceFilter: boolean;
  testId: string;
};

const InstrumentsActions = <T extends Record<string, unknown>>({
  defaultColumns,
  columns,
  preferenceKey,
  showPerformanceFilter,
  testId,
}: InstrumentsActionsProps<T>) => {
  const selectColumnsTestId = `${testId}_SelectColumnsButton`;
  return (
    <TopToolbar>
      <InstrumentsActionsItem>
        <FilterButton data-testid={`${testId}_FilterButton`} />
      </InstrumentsActionsItem>
      <InstrumentsActionsItem>
        <SelectColumnsButton<T>
          preference={preferenceKey}
          columns={columns}
          defaultColumns={defaultColumns}
          testId={selectColumnsTestId}
        />
      </InstrumentsActionsItem>
      {showPerformanceFilter && (
        <InstrumentsActionsItem>
          <PerformanceFilterButton data-testid={`${testId}_PerformanceFilterButton`} />
        </InstrumentsActionsItem>
      )}
    </TopToolbar>
  );
};

const hiddenFilters = ['type', 'performance'];

export function InstrumentsFundsList({ testId }: { testId: string }) {
  const columnsPreferenceKey = 'instruments.list.columns';
  const columns = useSelectedColumns({
    preferences: 'instruments.list.columns',
    defaultColumns,
  });
  const showPerformanceFilter = columns.includes('performance');

  return (
    <List
      resource="instruments"
      filters={instrumentFilters.filter((element) => !hiddenFilters.includes(element.props.source))}
      actions={
        <InstrumentsActions
          columns={instrumentsListColumns}
          defaultColumns={defaultColumns}
          preferenceKey={columnsPreferenceKey}
          showPerformanceFilter={showPerformanceFilter}
          testId={testId}
        />
      }
      sort={defaultSort}
      exporter={false}
      empty={false}>
      <DatagridTyped<GetInstrumentsResponseItem>
        sortableColumns={getInstrumentsSortColumns}
        rowClick="show"
        bulkActionButtons={false}
        displayedColumns={columns}
        columnRegistry={instrumentsListColumns}
      />
    </List>
  );
}
